import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Typography, CircularProgress, Tooltip } from "@mui/material";
import {
  get_school_dashboard_api,
  get_school_board_api,
  acknowledge_school_board_situation_api,
  get_work_queue_api,
  get_school_utilisation_api,
  get_school_run_rate_api,
  get_school_pulse_api,
} from "../../DAL/SchoolPortal/SchoolPortal";
import SchoolBoard from "../../components/SchoolBoard";
import WorkQueue from "./WorkQueue";
import DashboardPulse from "./DashboardPulse";
import { useSchool } from "../../Hooks/SchoolContext";
import { useContentSetting } from "../../Hooks/ContentSetting";
import {
  border, textPrimary, textSecondary, textMuted, accent, font,
  easing, success, warning, error as errorColor,
} from "../../layouts/schoolPortal/schoolPortalTheme";

/**
 * The dashboard is the work queue.
 *
 * WHAT WAS HERE BEFORE
 * Top 3 Subjects, Top 3 Students, a mini readiness donut and a Recent Activity
 * feed — four panels and zero decisions. The ranking was also broken: with four
 * or five qualifying students the same person appeared in both Top 3 and Needs
 * Attention, and `worst` was gated on `length > 3`, so a small school got a
 * filled good-news panel and a permanently empty bad-news one.
 *
 * THE INSTRUCTOR PANEL
 * It ranked named CFIs red/amber/green by notes logged in 30 days. A CFI who
 * teaches well and does not type showed red to their boss, on the school
 * owner's home page. Effectiveness metrics on named humans turn the supply side
 * against the product, and "notes logged" is not even a measure of teaching.
 * Replaced with utilisation — hours taught against the hours that instructor
 * declared, which is a capacity fact rather than a verdict on a person.
 *
 * WHAT SURVIVES
 * The Board's pattern is right and is left alone. Revenue is one header line
 * with a trend and a link, not the page.
 */

const money = (n, currency = "USD") =>
  new Intl.NumberFormat(currency === "GBP" ? "en-GB" : "en-US",
    { style: "currency", currency, maximumFractionDigits: 0 }).format(n || 0);

const utilColor = (pct) =>
  pct == null ? textMuted : pct >= 75 ? warning : pct >= 25 ? success : textSecondary;

function HeaderStat({ label, value, sub, color, onClick, tip }) {
  const body = (
    <Box
      onClick={onClick}
      sx={{
        px: 1.5, py: 1, borderRight: `1px solid ${border}`, cursor: onClick ? "pointer" : "default",
        transition: `background 0.12s ${easing}`, "&:last-of-type": { borderRight: "none" },
        "&:hover": onClick ? { backgroundColor: "#FAF8F5" } : {},
      }}
    >
      <Typography sx={{
        fontFamily: font, fontSize: "10.5px", fontWeight: 700, letterSpacing: "0.06em",
        textTransform: "uppercase", color: textSecondary,
      }}>
        {label}
      </Typography>
      <Typography sx={{ fontFamily: font, fontSize: "18px", fontWeight: 800, color: color || textPrimary, lineHeight: 1.2, fontVariantNumeric: "tabular-nums" }}>
        {value}
      </Typography>
      {sub && <Typography sx={{ fontFamily: font, fontSize: "11px", color: textSecondary }}>{sub}</Typography>}
    </Box>
  );
  return tip ? <Tooltip title={tip} arrow placement="bottom">{body}</Tooltip> : body;
}

export default function SchoolDashboard() {
  const navigate = useNavigate();
  const { isIndependentInstructor } = useSchool();
  // The viewer's role, for the diary's cancel controls. The server decides who
  // may actually cancel; this only decides what the dialog says.
  const { userInfo } = useContentSetting();
  const [isLoading, setIsLoading] = useState(true);
  // A panel that failed must say so — see the allSettled note below.
  const [loadError, setLoadError] = useState(null);

  const [dashboard, setDashboard] = useState(null);
  const [board, setBoard] = useState(null);
  const [queue, setQueue] = useState(null);
  const [utilisation, setUtilisation] = useState(null);
  const [runRate, setRunRate] = useState(null);
  // Work set across the school, the diary ahead, and revenue — the three
  // things the dashboard could only answer one student at a time.
  const [pulse, setPulse] = useState(null);
  // Cancelling or rescheduling from the diary changes the very figures above
  // it, so the panels reload rather than leaving a cancelled session sitting
  // in a confirmed count.
  const [refreshTick, setRefreshTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    // allSettled, not all: one failed call should degrade one panel, not blank
    // the page. And a panel that failed must SAY so — a bare `.catch(() => {})`
    // rendered a dashboard of zeros that looked like a real answer, on the
    // screen whose numbers set a contract price.
    //
    // invokeApi NEVER rejects — it catches and returns {code, message} — so a
    // settled status says nothing at all about whether the call worked.
    // The failure signal is the code, and 403 is not a failure: it is a module
    // this school did not buy, which the page should render as an absence
    // rather than as an error.
    Promise.allSettled([
      get_school_dashboard_api(),
      get_school_board_api("PPL"),
      get_work_queue_api(),
      get_school_utilisation_api(30),
      get_school_run_rate_api(6),
      get_school_pulse_api({ days: 14 }),
    ]).then((settled) => {
      if (cancelled) return;
      const value = (r) => (r.status === "fulfilled" ? r.value : null);
      const ok = (r) => {
        const v = value(r);
        // A response with no code at all is a legacy shape that returns its
        // payload directly; treat a truthy body as success rather than
        // reporting a false failure.
        return v ? (v.code == null || v.code === 200) : false;
      };
      const failed = settled.filter((r) => !ok(r) && value(r)?.code !== 403).length;
      setLoadError(failed ? `${failed} of ${settled.length} panels could not load. What you see below is incomplete.` : null);

      const [dash, board_, queue_, util_, rate_, pulse_] = settled;
      setDashboard(ok(dash) ? value(dash) : null);
      setBoard(ok(board_) ? value(board_)?.data : { situations: [], ongoing: [], allClear: true });
      setQueue(ok(queue_) ? value(queue_) : null);
      setUtilisation(ok(util_) ? value(util_) : null);
      setRunRate(ok(rate_) ? value(rate_) : null);
      setPulse(ok(pulse_) ? value(pulse_) : null);
    }).finally(() => {
      if (!cancelled) setIsLoading(false);
    });
    return () => { cancelled = true; };
  }, [refreshTick]);

  const handleAcknowledge = (situation) => {
    // Optimistic move from "new" to "ongoing" — don't make the CFI wait on
    // a round trip to see the item leave the top list.
    setBoard((prev) => {
      if (!prev) return prev;
      const situations = prev.situations.filter((s) => s.situationId !== situation.situationId);
      return { ...prev, situations, ongoing: [...prev.ongoing, situation] };
    });
    acknowledge_school_board_situation_api(situation.situationId, situation.type).catch(() => {});
  };

  if (isLoading) {
    return <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "60vh" }}><CircularProgress sx={{ color: accent }} /></Box>;
  }

  const counts = queue?.counts || {};
  const isInstructor = queue?.scope === "instructor";
  // `latest` and `trendPercent` are computed server-side; recomputing them
  // here is how the header and the report end up disagreeing.
  const thisMonth = runRate?.latest;
  const trendPct = runRate?.trendPercent ?? null;
  const currency = runRate?.currency || "USD";

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2 }, maxWidth: 1200, margin: "0 auto" }}>
      {loadError && (
        <Box sx={{ mb: 1.5, p: 1.25, borderRadius: "8px", border: `1px solid ${warning}`, background: `${warning}0d` }}>
          <Typography sx={{ fontFamily: font, fontSize: 12.5, color: warning, fontWeight: 600 }}>{loadError}</Typography>
        </Box>
      )}

      <Box sx={{ mb: 1.5 }}>
        <Typography sx={{ fontSize: "20px", fontWeight: 800, color: textPrimary, fontFamily: font, letterSpacing: "-0.2px" }}>
          {isInstructor || isIndependentInstructor ? "Your students" : "Dashboard"}
        </Typography>
        <Typography sx={{ fontSize: "12px", color: textSecondary, fontFamily: font, mt: 0.2 }}>
          {isInstructor || isIndependentInstructor ? "What needs you today" : "What needs you today, and how the school is running"}
        </Typography>
      </Box>

      {/* One row of facts, each one drillable. Revenue is a line here, not the
          page — the P&L lives in Bookings & Revenue. */}
      <Box sx={{
        display: "grid",
        gridTemplateColumns: { xs: "repeat(2, 1fr)", sm: "repeat(3, 1fr)", md: "repeat(5, 1fr)" },
        border: `1px solid ${border}`, borderRadius: "10px", backgroundColor: "#fff", mb: 1.5, overflow: "hidden",
      }}>
        <HeaderStat
          label="Needs you" value={counts.needingYou ?? 0} color={counts.needingYou ? errorColor : success}
          sub={counts.needingYou ? "open the list below" : "all clear"}
        />
        <HeaderStat
          label="Awaiting approval" value={counts.awaitingApproval ?? 0}
          color={counts.awaitingApproval ? warning : textPrimary}
          sub="blocking a student"
          tip="Drafted tasks and written session summaries a student cannot see until you sign them off."
        />
        <HeaderStat
          label="Next 36h" value={counts.sessionsNext36h ?? 0} sub="sessions booked"
        />
        <HeaderStat
          label={isInstructor ? "Your students" : "Students"} value={counts.students ?? dashboard?.totalStudents ?? 0}
          sub={dashboard?.activityPulse ? `${dashboard.activityPulse.pct}% active this week` : null}
          onClick={() => navigate("/school/students")}
          tip="Activity is context, not a headline — a student can be doing the work without logging in every week."
        />
        {!isInstructor && !isIndependentInstructor && (
          <HeaderStat
            label="Revenue this month"
            value={thisMonth ? money(thisMonth.revenue, currency) : "—"}
            color={trendPct == null ? textPrimary : trendPct >= 0 ? success : errorColor}
            sub={trendPct != null
              ? `${trendPct >= 0 ? "+" : ""}${trendPct}% vs last month`
              : runRate?.trendSuppressed
                ? "last month was too small to compare against"
                : "no prior month to compare"}
            onClick={() => navigate("/school/bookings")}
          />
        )}
      </Box>

      {/* The Board — the one thing that needs a 2-second glance. */}
      <SchoolBoard board={board} onAcknowledge={handleAcknowledge} />

      {/* The state of the school — work set and whether it is being done, what
          is in the diary, and what has been earned. Above the work queue,
          which is what to DO about it. */}
      <DashboardPulse pulse={pulse} role={userInfo?.role || null} onChanged={() => setRefreshTick((t) => t + 1)} />

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: isInstructor || isIndependentInstructor ? "1fr" : "minmax(0, 1fr) 300px" }, gap: 1.5, mt: 1.5 }}>
        <WorkQueue data={queue} navigate={navigate} />

        {!isInstructor && !isIndependentInstructor && (
          <Box sx={{ backgroundColor: "#fff", border: `1px solid ${border}`, borderRadius: "10px", p: 1.75 }}>
            <Typography sx={{ fontFamily: font, fontSize: "13px", fontWeight: 800, color: textPrimary, mb: 0.25 }}>
              Instructor capacity
            </Typography>
            <Typography sx={{ fontFamily: font, fontSize: "11.5px", color: textSecondary, mb: 1.25, lineHeight: 1.45 }}>
              Hours taught against the hours each instructor said they were available. Last {utilisation?.windowDays ?? 30} days.
            </Typography>

            {(utilisation?.instructors || []).map((i) => (
              <Box key={i.instructorId} sx={{ py: 0.6, borderTop: `1px solid ${border}`, "&:first-of-type": { borderTop: "none", pt: 0 } }}>
                <Box sx={{ display: "flex", alignItems: "baseline", gap: 1 }}>
                  <Typography sx={{ fontFamily: font, fontSize: "12.5px", fontWeight: 600, color: textPrimary, flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {i.name}
                  </Typography>
                  <Typography sx={{ fontFamily: font, fontSize: "12.5px", fontWeight: 800, color: utilColor(i.utilisation), fontVariantNumeric: "tabular-nums" }}>
                    {i.utilisation == null ? "—" : `${i.utilisation}%`}
                  </Typography>
                </Box>
                <Typography sx={{ fontFamily: font, fontSize: "11px", color: textSecondary }}>
                  {i.utilisation == null
                    ? "has not set working hours"
                    : `${i.taughtHours}h taught of ${i.declaredHours}h declared · ${i.sessions} session${i.sessions === 1 ? "" : "s"}`}
                </Typography>
              </Box>
            ))}

            {!(utilisation?.instructors || []).length && (
              <Typography sx={{ fontFamily: font, fontSize: "12px", color: textSecondary, lineHeight: 1.45 }}>
                No bookable instructors yet. This fills once someone turns on bookings and sets their working hours.
              </Typography>
            )}

            {utilisation?.spareHours != null && (utilisation?.instructors || []).length > 0 && (
              <Box sx={{ mt: 1.25, pt: 1, borderTop: `1px solid ${border}` }}>
                <Typography sx={{ fontFamily: font, fontSize: "12px", color: textPrimary, lineHeight: 1.45 }}>
                  <strong>{utilisation.spareHours}h</strong> of declared time went untaught.
                </Typography>
                {/* 4.8 attaches sample size and a caveat to every statistic
                    server-side. Showing the number without the caveat is how a
                    figure from one instructor becomes a claim about a school. */}
                {utilisation.cohort?.caveat && (
                  <Typography sx={{ fontFamily: font, fontSize: "10.5px", color: warning, mt: 0.3, lineHeight: 1.4 }}>
                    {utilisation.cohort.caveat}
                  </Typography>
                )}
              </Box>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
}
